/**
 * What a sign-up carries from one screen to the next.
 *
 * Three things travel: the plan the visitor picked on the pricing page, the
 * business name they typed on the register form, and the referral code a
 * partner's link brought them in with. All are only ever *hints* - the plan's
 * price, cycle and trial are read back from the catalogue wherever they are
 * shown, the code is re-checked against the server on every keystroke and
 * again when the account is created, and the server reads the plan row again
 * when the subscription is created. Nothing about money originates here.
 *
 * Why they have to be carried at all: sign-up is several screens long, and
 * with email verification switched on it can span a gap of hours and a second
 * tab. The query string carries a value from page to page and this storage
 * keeps it across the gaps between them; each is the other's fallback, so a
 * link opened fresh still has the query and a redirect that lost the query
 * still has the storage.
 *
 * The business name is here rather than left to the onboarding form because
 * the storefront address is reserved from it and cannot be changed afterwards
 * (see `TenantUpdateRequest` on the server, where the slug is deliberately not
 * editable). It therefore has to be the merchant's real choice, made before
 * the business row exists - which is before payment, since a subscription
 * belongs to a business.
 *
 * The referral code is somebody else's identifier, not the visitor's own -
 * which is why the server checks it over POST rather than in a URL (see
 * `app/api/v1/referrals.py`). It is kept on the same terms as the plan code
 * and cleared the moment sign-up finishes with it.
 */

const PLAN_KEY = 'neoncarts.selected_plan';
const BUSINESS_KEY = 'neoncarts.business_name';
const REFERRAL_KEY = 'neoncarts.referral_code';

/** Where a merchant with a plan in hand goes to authorise it. */
export const SUBSCRIBE_PATH = '/subscribe';

function read(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    // Private browsing or a locked-down browser: the query string still carries
    // the plan, and the onboarding form still asks for the name.
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // As above: losing this costs a re-typed field, never a wrong charge.
  }
}

function forget(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Nothing to clear if nothing could be stored.
  }
}

// --- The chosen plan --------------------------------------------------------

function normalisePlan(code: string | null | undefined): string | null {
  const cleaned = (code ?? '').trim().toUpperCase();
  return cleaned.length >= 2 && cleaned.length <= 32 ? cleaned : null;
}

export function rememberSelectedPlan(code: string | null | undefined): void {
  const cleaned = normalisePlan(code);
  if (cleaned) write(PLAN_KEY, cleaned);
}

export function readSelectedPlan(): string | null {
  return normalisePlan(read(PLAN_KEY));
}

export function clearSelectedPlan(): void {
  forget(PLAN_KEY);
}

/**
 * The plan for this page: the query string first, then what was remembered.
 * A code found in the query is remembered as a side effect, so the next page
 * in the flow has it even if it loses the query.
 */
export function selectedPlanFrom(search: URLSearchParams): string | null {
  const fromQuery = normalisePlan(search.get('plan'));
  if (fromQuery) {
    rememberSelectedPlan(fromQuery);
    return fromQuery;
  }
  return readSelectedPlan();
}

// --- The referral code ------------------------------------------------------

/**
 * The same shape the server accepts, so a code that could never be valid is
 * not carried across three screens to be rejected at the end of them.
 */
function normaliseReferral(code: string | null | undefined): string | null {
  const cleaned = (code ?? '').trim().toUpperCase();
  return /^[A-Z0-9_-]{3,32}$/.test(cleaned) ? cleaned : null;
}

export function rememberReferralCode(code: string | null | undefined): void {
  const cleaned = normaliseReferral(code);
  if (cleaned) write(REFERRAL_KEY, cleaned);
}

export function readReferralCode(): string | null {
  return normaliseReferral(read(REFERRAL_KEY));
}

export function clearReferralCode(): void {
  forget(REFERRAL_KEY);
}

/**
 * The referral code for this page: the query string first, then what was
 * remembered. The twin of `selectedPlanFrom`, and for the same reason - a
 * partner's link lands on whichever page they chose to share, and sign-up is
 * several screens and possibly an email round trip away from there.
 */
export function referralCodeFrom(search: URLSearchParams): string | null {
  const fromQuery = normaliseReferral(search.get('ref'));
  if (fromQuery) {
    rememberReferralCode(fromQuery);
    return fromQuery;
  }
  return readReferralCode();
}

// --- The business name ------------------------------------------------------

function normaliseName(name: string | null | undefined): string | null {
  const cleaned = (name ?? '').trim();
  return cleaned.length >= 2 && cleaned.length <= 160 ? cleaned : null;
}

export function rememberBusinessName(name: string | null | undefined): void {
  const cleaned = normaliseName(name);
  if (cleaned) write(BUSINESS_KEY, cleaned);
}

export function readBusinessName(): string | null {
  return normaliseName(read(BUSINESS_KEY));
}

export function clearBusinessName(): void {
  forget(BUSINESS_KEY);
}

// --- Paths ------------------------------------------------------------------

/**
 * `/register?plan=GROWTH` - the pricing page's call to action for a plan.
 *
 * Carries `ref` too when the visitor arrived through a partner's link, so the
 * code survives the hop even in a browser that stores nothing.
 */
export function registerPathFor(code: string, ref?: string | null): string {
  const path = `/register?plan=${encodeURIComponent(code)}`;
  return ref ? `${path}&ref=${encodeURIComponent(ref)}` : path;
}

/** `/subscribe?plan=GROWTH` - the payment screen for one chosen plan. */
export function subscribePathFor(code: string): string {
  return `${SUBSCRIBE_PATH}?plan=${encodeURIComponent(code)}`;
}
